import { AddressInterface, buildAddress, createEmptyAddress } from "./Address";
import { buildCompanyInfo, CompanyInterface } from "./Company";
import { buildContact, ContactInterface, createEmptyContact } from "./Contact";
import { buildShippingPreference, NoShippingPreference, ShippingPreferenceInterface } from "./ShippingPreference";
import { UserInterface } from "./User";

export interface OrderInterface {
    id: number,
    createdAt: Date,
    updatedAt: Date,
    company: CompanyInterface,
    orderedBy: {
        id: string,
        displayName: string,
        companyId?: number,
        email?: string,
    },
    customerPONum?: string,
    fishbowlSONum?: string,
    orderStatus: OrderStatusInterface,
    billingAddress: AddressInterface,
    billingContact: ContactInterface,
    shippingAddress: AddressInterface,
    shippingContact: ContactInterface,
    customerPOURL?: string,
    notes: Array<OrderNotesInterface>,
    authorizePaymentProfileId?: string,
    lastUpdatedBy: { id: string, displayName: string },
    isDropShip: boolean,
    preferredPackingListURL?: string,
    shippingPreference: ShippingPreferenceInterface,
    shippingAccountNumber?: string,
    orderItems: Array<OrderItemInterface>,
    demoOrder?: boolean,
    returningDemo?: boolean,
    commissionRepCompany?: CommissionRepCompanyInterface | null,
    // Set when the order was created from an orders-inbox request (ADR-023)
    customerRequestId?: number | null,
    customerRequestSubject?: string | null,
}

export interface CommissionRepCompanyInterface {
    id: number,
    name: string
}

export type ItemCondition = 'new' | 'like_new' | 'refurbished';

export interface OrderItemInterface {
    id: number,
    createdAt?: Date,
    updatedAt?: Date,
    quantity: number,
    condition: ItemCondition,
    product: {
        name: string,
        id: number,
        price: number,
        resolvedPrice?: number,
        bstockLikeNew: number,
        bstockRefurbished: number,
        inventory: number,
        sku: string
    },
    lastUpdatedBy?: { name: string, id: number },
    notes?: string,
    fillWithBstock?: boolean,
    bstockType?: string | null,
    // Manual unit-price override set by an internal user. When set it is the
    // final price and overrides all rule/condition pricing.
    overridePrice?: number | null,
    // Unit price stored on a saved order line. The API resolved rules and the
    // B-stock condition discount when the order was saved, so this is final
    // and must be shown as-is. Cart items have no saved price and are priced
    // live from product.resolvedPrice. Null on override lines (overridePrice
    // carries the stored value there).
    savedUnitPrice?: number | null,
    // Who set the override (only populated when an internal user fetches order
    // details; pre-save the current user is the implied modifier).
    priceModifiedBy?: { id: number, name: string } | null,
}

export interface OrderNotesInterface {
    id: number,
    createdAt?: Date,
    updatedAt?: Date,
    creator: { id: number, name: string },
    type: OrderNotesTypeInterface,
    content: string
}

export interface OrderNotesTypeInterface {
    id: number,
    name: string
}

// The company's "Primary" contact is the preferred default selection in checkout.
const findPrimaryContact = (company: CompanyInterface): ContactInterface =>
    (company.contacts || []).find((c) => (c.contactTypes || []).some((t) => t.name === "Primary"));

export const CreateEmptyOrder = (company: CompanyInterface, currentUser: UserInterface): OrderInterface => {
    if (!company) return null;
    let shippingAddress: AddressInterface = company.addresses.find((a) => a.addressTypes.find((at) => at.name == "Shipping"));
    // Prefer the Primary contact, then fall back to a Shipping-type contact / first contact.
    let shippingContact: ContactInterface = findPrimaryContact(company)
        || company.contacts.find((a) => a.contactTypes.find((at) => at.name == "Shipping"));

    if (!shippingAddress) shippingAddress = company.addresses.length ? company.addresses[0] : createEmptyAddress();
    if (!shippingContact) shippingContact = company.contacts.length ? company.contacts[0] : createEmptyContact();

    const tempOrder = {
        id: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        company: company,
        orderedBy: { id: currentUser.uid, displayName: currentUser.displayName },
        orderStatus: {
            id: 0,
            name: "New Order"
        },
        billingAddress: company.defaultBillingAddress,
        billingContact: company.defaultBillingContact || findPrimaryContact(company),
        shippingAddress: shippingAddress,
        shippingContact: shippingContact,
        lastUpdatedBy: { id: currentUser.uid, displayName: currentUser.displayName },
        isDropShip: false,
        shippingPreference: company.defaultShippingPreference ? company.defaultShippingPreference : NoShippingPreference,
        orderItems: [],
        notes: [],
        demoOrder: false
    };

    // Only adopt the shipping address's receiving contact when it actually
    // belongs to this company. Bad data can attach a foreign-company contact to
    // an address, which the order API rejects ("contact does not belong to the
    // order's company") and which would clobber the Primary default.
    const receivingContact = shippingAddress.additionalShippingDetails?.contact;
    if (receivingContact && (company.contacts || []).some((c) => c.id === receivingContact.id)) {
        tempOrder.shippingContact = receivingContact;
    }

    return tempOrder;
};

/**
 * Fill any missing shipping/billing address + contact on an order from the company's
 * data. Returns the same order reference unchanged if nothing was missing (so callers
 * can skip a no-op save). Used both when the cart loads and as a secondary catch when
 * the user reaches the Shipping/Billing checkout steps.
 */
export const fillOrderDefaults = (order: OrderInterface, company: CompanyInterface): OrderInterface => {
    if (!order || !company) return order;
    let updated = order;

    const addresses = company.addresses || [];
    const contacts = company.contacts || [];
    // A selection needs (re)defaulting when it's missing OR doesn't belong to the
    // order's company — the latter catches stale picks left over from a previous
    // company (e.g. after "order as"), which otherwise render blank in the pickers.
    // The validity checks below also accept legitimately off-list values (an
    // address's own receiving contact, the company's default billing contact/
    // address) so re-defaulting can never settle on a value that fails next pass —
    // which would loop forever via the saveCurrentOrder effect.
    const addrInCompany = (a) => !!(a && a.id && addresses.some((x) => x.id === a.id));
    const contactInCompany = (c) => !!(c && c.id && contacts.some((x) => x.id === c.id));
    const contactByType = (name: string) => contacts.find((x) => (x.contactTypes || []).some((t) => t.name === name));

    if (!addrInCompany(order.shippingAddress)) {
        const a = addresses.find((x) => (x.addressTypes || []).some((t) => t.name === "Shipping")) || addresses[0];
        if (a) updated = { ...updated, shippingAddress: a };
    }

    // The shipping address's receiving contact is only usable when it actually
    // belongs to this company — bad data can attach a foreign-company contact to
    // an address (which the order API rejects as "contact does not belong to the
    // order's company"). A same-company receiving contact is always in the
    // company's contact list, so contactInCompany is the right gate here.
    const receiving = updated.shippingAddress?.additionalShippingDetails?.contact;
    const validReceiving = contactInCompany(receiving) ? receiving : null;
    if (!contactInCompany(order.shippingContact)) {
        const c = validReceiving || findPrimaryContact(company) || contactByType("Shipping") || contacts[0];
        if (c) updated = { ...updated, shippingContact: c };
    }

    const billingAddrValid = addrInCompany(order.billingAddress)
        || !!(order.billingAddress?.id && company.defaultBillingAddress?.id === order.billingAddress.id);
    if (!billingAddrValid) {
        const a = company.defaultBillingAddress
            || addresses.find((x) => (x.addressTypes || []).some((t) => t.name === "Billing"));
        if (a) updated = { ...updated, billingAddress: a };
    }

    const billingContactValid = contactInCompany(order.billingContact)
        || !!(order.billingContact?.id && company.defaultBillingContact?.id === order.billingContact.id);
    if (!billingContactValid) {
        const c = company.defaultBillingContact
            || findPrimaryContact(company)
            || contactByType("Billing");
        if (c) updated = { ...updated, billingContact: c };
    }

    return updated;
};

export const BuildOrderItemObject = (rawData: any): OrderItemInterface => {
    const bstockType = rawData.bstock_type || rawData.bstockType || null;
    const condition: ItemCondition = bstockType === 'like_new' || bstockType === 'refurbished'
        ? bstockType
        : 'new';

    // The stored line price already includes rule pricing and the B-stock
    // condition discount. It must NOT be fed back in as the product's base or
    // resolved price: effectiveUnitPrice applies the condition discount to
    // those, which discounted B-stock lines twice on every saved order from
    // 6/19/26 to 10/7/26 (portal order 1865 showed $906.88 for a $1,133.60 line).
    const storedPrice = rawData.price != null ? Number(rawData.price) : null;
    const hasOverride = rawData.overridePrice != null || !!rawData.price_modified_by;

    return {
        id: rawData.id,
        createdAt: rawData.created_at ? new Date(rawData.created_at) : null,
        updatedAt: rawData.updated_at ? new Date(rawData.updated_at) : null,
        quantity: rawData.quantity,
        condition,
        product: {
            id: rawData.product.id,
            name: rawData.product.name,
            // Product list price. Only a fallback for lines with no stored
            // price (legacy rows) or an override that was just cleared.
            price: rawData.product.price ?? storedPrice,
            resolvedPrice: rawData.product.resolvedPrice ?? undefined,
            bstockLikeNew: rawData.product.inventory_bstock_like_new ?? rawData.product.bstockLikeNew ?? 0,
            bstockRefurbished: rawData.product.inventory_bstock_refurbished ?? rawData.product.bstockRefurbished ?? 0,
            inventory: rawData.product.inventory,
            sku: rawData.product.sku
        },
        lastUpdatedBy: rawData.lastUpdatedBy ? {
            id: rawData.lastUpdatedBy.id,
            name: rawData.lastUpdatedBy.name
        } : null,
        notes: rawData.notes,
        fillWithBstock: rawData.fill_with_bstock ?? rawData.fillWithBstock ?? false,
        bstockType,
        // A manual price override exists when the API returns price_modified_by.
        // The stored item price IS that override. (localStorage carts already
        // carry overridePrice/priceModifiedBy in camelCase, so honor those too.)
        overridePrice: rawData.overridePrice ?? (rawData.price_modified_by ? (rawData.price ?? null) : null),
        priceModifiedBy: rawData.priceModifiedBy ?? (rawData.price_modified_by
            ? { id: rawData.price_modified_by.id, name: rawData.price_modified_by.name }
            : null),
        savedUnitPrice: hasOverride ? null : storedPrice,
    };
};



export const BuildOrderObjectForList = (rawOrderData: any, companyList: Array<CompanyInterface>): OrderInterface => {
    let company = null;
    let billingAddress = null;
    let billingContact = null;
    let shippingAddress = null;
    let shippingContact = null;
    
    if(companyList != null && companyList.length > 0) {
        company = companyList.find((c) => c.id == rawOrderData.company_id);
        if(!company) {
            console.log("Couldn't find company data associated with this order");
            return null;
        }
        billingAddress = company.addresses ? company.addresses.find((a) => a.id == rawOrderData.billing_address_id) : null;
        billingContact = company.contacts ? company.contacts.find((c) => c.id == rawOrderData.billing_contact_id) : null;
        shippingAddress = company.addresses ? company.addresses.find((a) => a.id == rawOrderData.shipping_address_id) : null;
        shippingContact = company.contacts ? company.contacts.find((c) => c.id == rawOrderData.shipping_contact_id) : null;
    } else {
        if(rawOrderData.billing_address) {
            billingAddress = buildAddress(rawOrderData.billing_address);
        }
        
        if(rawOrderData.shipping_address) {
            shippingAddress = buildAddress(rawOrderData.shipping_address);
        }
        
        if(rawOrderData.billing_contact) {
            billingContact = buildContact(rawOrderData.billing_contact);
        }
        
        if(rawOrderData.shipping_contact) {
            shippingContact = buildContact(rawOrderData.shipping_contact);
        }
    }
    
    const orderItems = [];
    for (let ori = 0; ori < rawOrderData.order_items.length; ori++) {
        const orderItemData = rawOrderData.order_items[ori];
        const orderItem = BuildOrderItemObject(orderItemData);

        orderItems.push(orderItem);
    }

    return {
        id: rawOrderData.id,
        createdAt: new Date(rawOrderData.created_at),
        updatedAt: new Date(rawOrderData.updated_at),
        company: company ?? rawOrderData.company,
        orderedBy: {
            ...rawOrderData.ordered_by,
            companyId: rawOrderData.ordered_by.associated_company_id,
            displayName: rawOrderData.ordered_by.name,
            email: rawOrderData.ordered_by.email,
        },
        customerPONum: rawOrderData.customer_po_num,
        fishbowlSONum: rawOrderData.fishbowl_so_num,
        orderStatus: rawOrderData.order_status,
        billingAddress: billingAddress,
        billingContact: billingContact,
        shippingAddress: shippingAddress,
        shippingContact: shippingContact,
        customerPOURL: rawOrderData.customer_po_upload_url,
        notes: rawOrderData.notes ? buildOrderNotes(rawOrderData.notes) : [],
        authorizePaymentProfileId: rawOrderData.authorize_payment_profile_id,
        lastUpdatedBy: { ...rawOrderData.last_updated_by, displayName: rawOrderData.last_updated_by.name },
        isDropShip: rawOrderData.drop_ship,
        preferredPackingListURL: rawOrderData.preferred_packing_list_upload_url,
        shippingPreference: rawOrderData.shipping_preference,
        shippingAccountNumber: rawOrderData.shipping_account_number,
        demoOrder: rawOrderData.demo_order ?? false,
        returningDemo: rawOrderData.returning_demo ?? false,
        orderItems: orderItems
    };
};

export const BuildOrdersList = (rawOrderData: Array<any>, companyList: Array<CompanyInterface>): Array<OrderInterface> => {

    const orderList: Array<OrderInterface> = [];

    for (let rodi = 0; rodi < rawOrderData.length; rodi++) {
        const orderData = rawOrderData[rodi];
        const orderObject = BuildOrderObjectForList(orderData, companyList);
        if(orderObject) orderList.push(orderObject);
    }

    return orderList;
};

export const buildOrderNotes = (rawNoteData): Array<OrderNotesInterface> => {

    const noteList: Array<OrderNotesInterface> = [];

    if (!rawNoteData) return noteList;

    for (let rodi = 0; rodi < rawNoteData.length; rodi++) {
        const noteData = rawNoteData[rodi];
        noteList.push({
            id: noteData.id,
            createdAt: new Date(noteData.created_at),
            updatedAt: new Date(noteData.updated_at),
            creator: noteData.creator ? {
                id: noteData.creator.id,
                name: noteData.creator.first_name + " " + noteData.creator.last_name
            } : null,
            type: noteData.type,
            content: noteData.content
        });
    }

    return noteList;
};

export interface OrderStatusInterface {
    id: number,
    name: string
}

// export interface PONotesInterface {
//     shippingOption: string,
//     other: string
// }

export const BuildOrderObject = (rawData): OrderInterface => {
    const orderItems = [];
    for (let ori = 0; ori < rawData.order_items.length; ori++) {
        const orderItemData = rawData.order_items[ori];
        const orderItem = BuildOrderItemObject(orderItemData);

        orderItems.push(orderItem);
    }
    return {
        id: rawData.id,
        createdAt: new Date(rawData.created_at),
        updatedAt: new Date(rawData.updated_at),
        company: buildCompanyInfo(rawData.company),
        orderedBy: {
            ...rawData.ordered_by,
            companyId: rawData.ordered_by.associated_company_id,
            displayName: rawData.ordered_by.name,
            email: rawData.ordered_by.email,
        },
        customerPONum: rawData.customer_po_num ? rawData.customer_po_num : null,
        fishbowlSONum: rawData.fishbowl_so_num,
        orderStatus: BuildOrderStatusObject(rawData.order_status),
        billingAddress: buildAddress(rawData.billing_address),
        billingContact: buildContact(rawData.billing_contact),
        shippingAddress: buildAddress(rawData.shipping_address),
        shippingContact: buildContact(rawData.shipping_contact),
        customerPOURL: rawData.customer_po_upload_url,
        notes: buildOrderNotes(rawData.notes),
        authorizePaymentProfileId: rawData.authorize_payment_profile_id,
        lastUpdatedBy: { ...rawData.last_updated_by, displayName: rawData.last_updated_by.name },
        isDropShip: rawData.drop_ship,
        preferredPackingListURL: rawData.preferred_packing_list_upload_url,
        shippingPreference: buildShippingPreference(rawData.shipping_preference),
        demoOrder: rawData.demo_order ?? false,
        returningDemo: rawData.returning_demo ?? false,
        commissionRepCompany: rawData.commission_rep_company ?? null,
        customerRequestId: rawData.customer_request_id ?? null,
        customerRequestSubject: rawData.customer_request?.subject ?? null,
        orderItems: orderItems
    };
};

export const BuildOrderStatusObject = (rawData): OrderStatusInterface => {
    return {
        id: rawData.id,
        name: rawData.name,
    };
};

export const BuildOrderStatusOptions = (rawData): Array<OrderStatusInterface> => {
    const options = [];
    for (let osoi = 0; osoi < rawData.length; osoi++) {
        const optionData = rawData[osoi];
        options.push(BuildOrderStatusObject(optionData));
    }

    return options;
};